import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { API_URL } from '../../utils/api';
import { fetchAvatarsBulk } from '../../utils/avatars';
import { fetchCountriesBulk } from '../../utils/countries';
import { formatNumber, getRatingClass } from '../../utils/formatters';
import { getRankTier } from '../elo/constants';
import useDebounce from '../../hooks/useDebounce';
import useMediaQuery from '../../hooks/useMediaQuery';
import './PlayersList.css';
import RankShield from '../elo/RankShield';
import RankLegend from '../elo/RankLegend';
import RatingInfo from '../common/RatingInfo';
import SteamAvatar from '../common/SteamAvatar';
import SteamFlag from '../common/SteamFlag';
import { PlayersListSkeleton } from '../common/Skeleton';

// Emoji per inferred playstyle role (Fas 4), mirrors PlayerStatsSections.
const ROLE_ICONS = {
  'AWPer': '🎯',
  'Entry Fragger': '⚡',
  'Support': '💨',
  'Lurker': '🥷',
  'Rifler': '🔫',
};

// Roles offered in the filter dropdown — the playstyle labels _compute_role
// can return (Fas 4). "Unranked" (< 3 matches) is excluded from the filter.
const ROLE_FILTER_OPTIONS = ['AWPer', 'Entry Fragger', 'Support', 'Lurker', 'Rifler'];

/**
 * Stat columns selectable as the phone view's fourth column. Keys double as
 * player-object fields AND sort keys for handleSort. `render` returns the
 * cell content; `highlight` mirrors the desktop K/D styling.
 */
const MOBILE_STAT_COLUMNS = {
  rating: {
    label: 'Rating',
    render: (p) => (
      <span className={`rating-badge ${getRatingClass(p.rating)}`}>{p.rating}</span>
    ),
  },
  kd_ratio: { label: 'K/D', highlight: true, render: (p) => p.kd_ratio },
  avg_adr: { label: 'ADR', render: (p) => p.avg_adr },
  headshot_percentage: { label: 'HS%', render: (p) => `${p.headshot_percentage}%` },
  total_kills: { label: 'Kills', render: (p) => formatNumber(p.total_kills) },
  total_deaths: { label: 'Deaths', render: (p) => formatNumber(p.total_deaths) },
  total_matches: { label: 'Matcher', labelKey: 'players.colMatches', render: (p) => p.total_matches || 0 },
  ud_per_round: { label: 'UD/R', render: (p) => p.ud_per_round || 0 },
};

// localStorage key for the phone stat-column choice (persists across visits).
const MOBILE_STAT_STORAGE_KEY = 'players_mobile_stat';

/**
 * Sortable, filterable list of all players with ELO and demo-derived stats.
 *
 * Desktop renders all eleven columns. On phones (≤480px, via useMediaQuery)
 * the table shows # · Player · ELO plus ONE user-selected stat column,
 * chosen with a dropdown in the filter row and persisted in localStorage.
 * Tablet (≤768px) column trimming stays CSS-driven in PlayersList.css.
 */
const PlayersList = ({ onSelectPlayer, onSelectH2H }) => {
  const { t } = useTranslation();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'elo', direction: 'desc' });
  const [compareMode, setCompareMode] = useState(false);
  const [compareFirst, setCompareFirst] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [minMatches, setMinMatches] = useState(0);
  const debouncedSearch = useDebounce(search, 150);
  const isPhone = useMediaQuery('(max-width: 480px)');
  // Validate the stored choice so a stale/garbage value falls back to rating.
  const [mobileStat, setMobileStat] = useState(() => {
    const stored = localStorage.getItem(MOBILE_STAT_STORAGE_KEY);
    return MOBILE_STAT_COLUMNS[stored] ? stored : 'rating';
  });

  const handleMobileStatChange = (key) => {
    setMobileStat(key);
    localStorage.setItem(MOBILE_STAT_STORAGE_KEY, key);
  };

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/players`).then(r => r.json()),
      fetch(`${API_URL}/api/elo/leaderboard`).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/stats/roles`).then(r => r.json()).catch(() => ({ roles: {} })),
    ]).then(async ([playerData, eloData, rolesData]) => {
      const playerList = playerData.players || [];
      const eloList = Array.isArray(eloData) ? eloData : [];
      const rolesMap = (rolesData && rolesData.roles) || {};

      const eloMap = {};
      eloList.forEach(e => {
        if (e.steamid64) eloMap[e.steamid64] = e;
      });

      const merged = playerList.map(p => {
        const elo = eloMap[p.steamid64];
        const roleInfo = rolesMap[p.steamid64];
        return {
          ...p,
          elo: elo ? elo.elo : null,
          elo_rank: elo ? elo.rank : null,
          current_streak: elo ? elo.current_streak : 0,
          role: roleInfo ? roleInfo.role : null,
          ud_per_round: roleInfo ? roleInfo.ud_per_round : 0,
        };
      });

      const steamids = merged.map(p => p.steamid64);
      await Promise.all([fetchAvatarsBulk(steamids), fetchCountriesBulk(steamids)]);
      setPlayers(merged);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const sortedPlayers = [...players].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (sortConfig.key === 'name') {
      const sA = aValue ? String(aValue).toLowerCase() : '';
      const sB = bValue ? String(bValue).toLowerCase() : '';
      return sortConfig.direction === 'asc'
        ? sA.localeCompare(sB, 'sv')
        : sB.localeCompare(sA, 'sv');
    }

    const numA = Number(aValue) || 0;
    const numB = Number(bValue) || 0;
    if (sortConfig.key === 'elo') {
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;
    }

    return sortConfig.direction === 'asc'
      ? numA > numB ? 1 : -1
      : numA < numB ? 1 : -1;
  });

  // Apply the search / role / min-matches filters after sorting so the
  // displayed rank index reflects the active filter set (Fas 11B).
  const q = debouncedSearch.trim().toLowerCase();
  const filteredPlayers = sortedPlayers.filter((p) => {
    if (q && !(p.name || '').toLowerCase().includes(q)) return false;
    if (roleFilter !== 'all' && p.role !== roleFilter) return false;
    if (minMatches && (p.total_matches || 0) < minMatches) return false;
    return true;
  });
  const filtersActive = q || roleFilter !== 'all' || minMatches > 0;

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <span style={{opacity: 0.3}}>↕️</span>;
    return sortConfig.direction === 'desc' ? '🔽' : '🔼';
  };

  if (loading) return (
    <div className="players-list">
      <PlayersListSkeleton />
    </div>
  );

  return (
    <div className="players-list">
      <div className="list-header">
        <h2 className="section-heading">All Players</h2>
        <div className="sort-options">
          {onSelectH2H && (
            <button
              className={`compare-toggle ${compareMode ? 'active' : ''}`}
              onClick={() => { setCompareMode(!compareMode); setCompareFirst(null); }}
            >
              {compareMode ? t('players.cancelCompare') : t('players.compare')}
            </button>
          )}
          <RankLegend />
          <RatingInfo />
        </div>
      </div>
      {compareMode && (
        <div className="compare-instructions">
          {compareFirst ? t('players.pickSecond') : t('players.pickFirst')}
        </div>
      )}
      <div className="players-filters">
        <input
          type="text"
          className="players-search"
          placeholder={`🔎 ${t('players.searchPlaceholder')}`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('players.searchPlaceholder')}
        />
        <select
          className="players-filter-select"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          aria-label={t('players.roleFilterAria')}
        >
          <option value="all">{t('players.allRoles')}</option>
          {ROLE_FILTER_OPTIONS.map((r) => (
            <option key={r} value={r}>{`${ROLE_ICONS[r] || ''} ${r}`.trim()}</option>
          ))}
        </select>
        <select
          className="players-filter-select"
          value={minMatches}
          onChange={(e) => setMinMatches(Number(e.target.value))}
          aria-label={t('players.minMatchesAria')}
        >
          <option value={0}>{t('players.allMatches')}</option>
          <option value={5}>{t('players.minMatchesOption', { count: 5 })}</option>
          <option value={10}>{t('players.minMatchesOption', { count: 10 })}</option>
          <option value={25}>{t('players.minMatchesOption', { count: 25 })}</option>
        </select>
        {isPhone && (
          <select
            className="players-filter-select"
            value={mobileStat}
            onChange={(e) => handleMobileStatChange(e.target.value)}
            aria-label={t('players.statColumnAria')}
          >
            {Object.entries(MOBILE_STAT_COLUMNS).map(([key, col]) => (
              <option key={key} value={key}>{`${t('players.columnPrefix')}: ${col.labelKey ? t(col.labelKey) : col.label}`}</option>
            ))}
          </select>
        )}
        {filtersActive && (
          <button
            type="button"
            className="players-filter-clear"
            onClick={() => { setSearch(''); setRoleFilter('all'); setMinMatches(0); }}
          >
            {t('players.clear')}
          </button>
        )}
        <span className="players-filter-count">
          {filteredPlayers.length} av {players.length}
        </span>
      </div>
      <div className="players-table">
        <div className="table-header">
          <span className="col-rank">#</span>
          <span className="col-player">{t('nav.players')}</span>
          <span className="col-stat sortable" onClick={() => handleSort('elo')} style={{cursor: 'pointer'}}>
            ELO {getSortIcon('elo')}
          </span>
          {isPhone ? (
            <span className="col-stat sortable" onClick={() => handleSort(mobileStat)}>
              {MOBILE_STAT_COLUMNS[mobileStat].labelKey ? t(MOBILE_STAT_COLUMNS[mobileStat].labelKey) : MOBILE_STAT_COLUMNS[mobileStat].label} {getSortIcon(mobileStat)}
            </span>
          ) : (
            <>
              <span className="col-stat sortable" onClick={() => handleSort('total_matches')} style={{cursor: 'pointer'}}>
                {t('players.colMatches')} {getSortIcon('total_matches')}
              </span>
              <span className="col-stat sortable" onClick={() => handleSort('total_kills')} style={{cursor: 'pointer'}}>
                Kills {getSortIcon('total_kills')}
              </span>
              <span className="col-stat sortable" onClick={() => handleSort('total_deaths')} style={{cursor: 'pointer'}}>
                Deaths {getSortIcon('total_deaths')}
              </span>
              <span className="col-stat sortable" onClick={() => handleSort('kd_ratio')} style={{cursor: 'pointer'}}>
                K/D {getSortIcon('kd_ratio')}
              </span>
              <span className="col-stat sortable" onClick={() => handleSort('headshot_percentage')} style={{cursor: 'pointer'}}>
                HS% {getSortIcon('headshot_percentage')}
              </span>
              <span className="col-stat sortable" onClick={() => handleSort('avg_adr')} style={{cursor: 'pointer'}}>
                ADR {getSortIcon('avg_adr')}
              </span>
              <span className="col-stat sortable" onClick={() => handleSort('rating')} style={{cursor: 'pointer'}}>
                Rating {getSortIcon('rating')}
              </span>
              <span className="col-stat sortable" onClick={() => handleSort('ud_per_round')} style={{cursor: 'pointer'}} title="Utility-skada per runda">
                UD/R {getSortIcon('ud_per_round')}
              </span>
            </>
          )}
        </div>

        {filteredPlayers.length === 0 && (
          <div className="players-empty">{t('players.noMatch')}</div>
        )}

        {filteredPlayers.map((player, index) => {
          const tier = player.elo ? getRankTier(player.elo) : null;
          return (
            <div
              key={player.steamid64}
              className={`table-row ${compareFirst === player.steamid64 ? 'compare-selected-row' : ''}`}
              onClick={() => {
                if (compareMode && onSelectH2H) {
                  if (!compareFirst) {
                    setCompareFirst(player.steamid64);
                  } else if (compareFirst !== player.steamid64) {
                    onSelectH2H(compareFirst, player.steamid64);
                    setCompareMode(false);
                    setCompareFirst(null);
                  }
                } else {
                  onSelectPlayer(player.steamid64);
                }
              }}
            >
              <span className="col-rank">{index + 1}</span>
              <span className="col-player">
                <SteamAvatar steamid64={player.steamid64} name={player.name} size="small" />
                <SteamFlag steamid64={player.steamid64} className="players-list-flag" />
                {player.name}
                {player.role && player.role !== 'Unranked' && (
                  <span className="player-role-badge" title={player.role}>
                    {ROLE_ICONS[player.role] || '🎮'}
                  </span>
                )}
                {player.current_streak >= 3 && ' 🔥'}
                {player.current_streak <= -3 && ' ❄️'}
              </span>
              <span className="col-stat col-elo-compact">
                {tier ? (
                  <span className="elo-compact" style={{ color: tier.color }} title={`${tier.name} — ${player.elo} ELO`}>
                    <RankShield tier={tier} size={18} />
                    <span className="elo-compact-value">{player.elo}</span>
                  </span>
                ) : (
                  <span className="elo-compact-unranked">—</span>
                )}
              </span>
              {isPhone ? (
                <span className={`col-stat${MOBILE_STAT_COLUMNS[mobileStat].highlight ? ' highlight' : ''}`}>
                  {MOBILE_STAT_COLUMNS[mobileStat].render(player)}
                </span>
              ) : (
                <>
                  <span className="col-stat">{player.total_matches || 0}</span>
                  <span className="col-stat">{formatNumber(player.total_kills)}</span>
                  <span className="col-stat">{formatNumber(player.total_deaths)}</span>
                  <span className="col-stat highlight">{player.kd_ratio}</span>
                  <span className="col-stat">{player.headshot_percentage}%</span>
                  <span className="col-stat">{player.avg_adr}</span>
                  <span className="col-stat">
                    <span className={`rating-badge ${getRatingClass(player.rating)}`}>
                      {player.rating}
                    </span>
                  </span>
                  <span className="col-stat">{player.ud_per_round || 0}</span>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlayersList;
